import React, { useState } from 'react';
import { Input, Modal, Button, Divider, notification, Tooltip, Switch } from 'antd';
import { CopyOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { AppRoutes } from 'src/enums/ERoutes';
import { ChevronRight, CircleDollarSign, PlusCircle, SparklesIcon, Tag, User } from 'lucide-react';
import { Reseller } from '@/types/TReseller';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
	ResellerDashboardLink,
	UpdateAutoScaleReceptionist,
	updateDefaultRegistration,
} from '@/api/reseller';
import { openModal } from '@/store/modal/slice.ts';
import { useAppDispatch } from '@/store/hooks/useAppDispatch';
import { MODAL_IDS } from 'src/enums/EModal';
import { updateReseller } from '@/store/reseller/slice';

type Props = {
	resellerData: Reseller;
};

const Embed: React.FC<Props> = ({ resellerData }) => {
	const [embedRegisterOptionsModal, setEmbedRegisterOptionsModal] = useState(false);
	const [registrationType, setRegistrationType] = useState<'trial' | 'paid' | null>(null);
	const [showFeatureGating, setShowFeatureGating] = useState<boolean>(false);
	const [autoScaleEnabled, setAutoScaleEnabled] = useState<boolean>(
		resellerData.auto_scale_receptionists || false,
	);
	const [showAutoScale, setShowAutoScale] = useState<boolean>(false);
	const [embedType, setEmbedType] = useState<'login' | 'register' | null>(null);
	const [isAutoScaleUpdating, setIsAutoScaleUpdating] = useState(false);
	const [defaultRegisterPageLoading, setDefaultRegisterPageLoading] = useState(false);
	const dispatch = useAppDispatch();

	const { data: linkData } = useQuery({
		queryKey: ['getResellerDashboardLink', resellerData.id],
		queryFn: () => ResellerDashboardLink(),
	});

	const handleCopyIframe = async (route: string) => {
		if (linkData?.link) {
			try {
				const iframeUrl = `${import.meta.env.VITE_APP_WIHTELABEL_URL}${route}?code=${linkData.link}`;
				const iframeString = `<iframe src="${iframeUrl}" width="100%" height="100%" frameBorder="0" style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            width: '100%',
            height: '100%',
            border: 'none',
            margin: 0,
            padding: 0,
            overflow: 'hidden',
            zIndex: 999999,
          }}></iframe>`;

				await navigator.clipboard.writeText(iframeString);
				notification.success({ message: 'iframe copied successfully' });
			} catch {
				notification.error({ message: 'Failed to copy iframe' });
			}
		} else {
			notification.error({ message: 'No link available to copy' });
		}
	};

	// Copy the embed link
	const handleTrialCopyIframe = async () => {
		if (linkData?.link) {
			try {
				const iframeUrl = `${import.meta.env.VITE_APP_WIHTELABEL_URL}${AppRoutes.UNBRANDED_CLIENT_TRIAL}?code=${linkData.link}`;
				const iframeString = `<iframe src="${iframeUrl}" width="100%" height="100%" frameBorder="0" style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              height: '100%',
              border: 'none',
              margin: 0,
              padding: 0,
              overflow: 'hidden',
              zIndex: 999999,
            }}></iframe>`;

				await navigator.clipboard.writeText(iframeString);
				notification.success({ message: 'iframe copied successfully' });
			} catch {
				notification.error({ message: 'Failed to copy iframe' });
			}
		} else {
			notification.error({ message: 'No link available to copy' });
		}
	};

	const { mutate: updateDefaultRegistrationMutation, isPending } = useMutation({
		mutationKey: ['updateDefaultRegistration'],
		mutationFn: updateDefaultRegistration,
		onSuccess: (data) => {
			notification.success({ message: data.message });
			setDefaultRegisterPageLoading(false);
		},
		onError: () => {
			notification.error({
				message: 'Error',
				description: 'An error occurred while updating the domain. Please try again!',
			});
		},
	});

	const updateAutoScaleReceptionistMutation = useMutation({
		mutationKey: ['UpdateAutoScaleReceptionist'],
		mutationFn: (newAutoScaleValue: boolean) =>
			UpdateAutoScaleReceptionist({
				reseller_email: resellerData.emailAddress as string,
				auto_scale: newAutoScaleValue,
				customerId: resellerData.customerId,
			}),
	});

	const handleAutoScaleChange = (checked: boolean) => {
		setIsAutoScaleUpdating(true);
		setAutoScaleEnabled(checked);
		dispatch(updateReseller({ auto_scale_receptionists: checked }));
		updateAutoScaleReceptionistMutation.mutate(checked, {
			onSuccess: () => {
				setIsAutoScaleUpdating(false);
				notification.success({
					message: 'Auto scale updated',
					duration: 3,
				});
			},
			onError: () => {
				setIsAutoScaleUpdating(false);
				setAutoScaleEnabled(!checked); // Revert the switch if API fails
				dispatch(updateReseller({ auto_scale_receptionists: !checked }));
				notification.error({
					message: 'Failed to update auto scale setting',
				});
			},
		});
	};

	return (
		<>
			<>
				<h3 className="mb-4 font-semibold text-xl">Choose which page to integrate</h3>
				<p className="text-sm mb-4">
					Select whether you want to embed login or registration pages on your website.
				</p>

				<a
					onClick={() => {
						setEmbedType('login');
						setEmbedRegisterOptionsModal(true);
					}}
				>
					<div className="border-2 border-gray-300 mt-3 p-4 rounded-lg">
						<div className="flex w-full justify-between items-center mb-3">
							<User className="text-blue-600" />
							<ChevronRight className="text-gray-600 text-sm" size={18} />
						</div>
						<h4 className="text-base mb-0">Login Form</h4>
						<p className="my-0 text-gray-600 text-sm">Embed client login form on your website</p>
					</div>
				</a>

				<a
					onClick={() => {
						setEmbedType('register');
						setEmbedRegisterOptionsModal(true);
					}}
				>
					<div className="border-2 border-gray-300 mt-3 p-4 rounded-lg">
						<div className="flex w-full justify-between items-center mb-3">
							<PlusCircle className="text-blue-600" />
							<ChevronRight className="text-gray-600 text-sm" size={18} />
						</div>
						<h4 className="text-base mb-0">Registration Form</h4>
						<p className="my-0 text-gray-600 text-sm">
							Embed client registration form on your website
						</p>
					</div>
				</a>
			</>

			{/* Registration Options Modal */}
			<Modal
				open={embedRegisterOptionsModal}
				footer={null}
				onCancel={() => {
					setEmbedRegisterOptionsModal(false);
					setRegistrationType(null);
					setShowFeatureGating(false);
					setShowAutoScale(false);
					setEmbedType(null);
				}}
			>
				{!embedType ? (
					<></>
				) : embedType === 'login' ? (
					<>
						<h3 className="mb-4 font-semibold text-xl">Customer Login Integration</h3>
						<p className="text-sm mb-4">
							Allow your customers to access their dashboard directly from your website.
						</p>
						{resellerData.customDomain && (
							<>
								<div className="mb-6">
									<p className="text-sm font-medium mb-2">Custom Domain URL</p>
									<Input
										value={`https://${resellerData.customDomain}/login`}
										readOnly
										suffix={
											<CopyOutlined
												className="cursor-pointer text-gray-500 hover:text-gray-700"
												onClick={() => {
													if (linkData?.link) {
														navigator.clipboard.writeText(
															`https://${resellerData.customDomain}/login`,
														);
														notification.success({ message: 'URL copied successfully' });
													}
												}}
											/>
										}
									/>
								</div>
								<Divider />
							</>
						)}

						<div className="mb-6">
							<p className="text-sm font-medium mb-2">Direct URL</p>
							<Input
								value={`${import.meta.env.VITE_APP_WIHTELABEL_URL}${AppRoutes.UNBRANDED_DASHBOARD}?code=${linkData?.link}`}
								readOnly
								suffix={
									<CopyOutlined
										className="cursor-pointer text-gray-500 hover:text-gray-700"
										onClick={() => {
											if (linkData?.link) {
												navigator.clipboard.writeText(
													`${import.meta.env.VITE_APP_WIHTELABEL_URL}${AppRoutes.UNBRANDED_DASHBOARD}?code=${linkData.link}`,
												);
												notification.success({ message: 'URL copied successfully' });
											}
										}}
									/>
								}
							/>
						</div>

						<Divider />

						<div>
							<p className="text-sm font-medium mb-2">Embed Iframe</p>
							<p className="text-sm text-gray-600 mb-3">
								Copy and paste this code to embed the login form directly on your website
							</p>
							<Input
								value={`<iframe src="${import.meta.env.VITE_APP_WIHTELABEL_URL}${AppRoutes.UNBRANDED_DASHBOARD}?code=${linkData?.link}" width="100%" height="100%" frameBorder="0"></iframe>`}
								readOnly
								suffix={
									<CopyOutlined
										className="cursor-pointer text-gray-500 hover:text-gray-700"
										onClick={() => handleCopyIframe(AppRoutes.UNBRANDED_DASHBOARD)}
									/>
								}
							/>
						</div>

						{resellerData.customDomain && (
							<>
								<Divider className="my-4" />
								<div className="flex justify-between items-center border-2 border-gray-300 mt-3 p-4 rounded-lg">
									<div>
										<h4 className="text-base mb-0">
											Default Registration{' '}
											<Tooltip title="If a prospect arrives to your login page, you can decide whether they would be redirected to create a free trial with 15 test free calls, or a paid account when they click on Register now.">
												<InfoCircleOutlined />
											</Tooltip>
										</h4>
										<span>Redirect potential clients when they click on Register now </span>
									</div>
									<Switch
										onChange={(checked) => {
											setDefaultRegisterPageLoading(true);
											updateDefaultRegistrationMutation({
												defaultRegistration: checked ? 'Paid' : 'Trial',
											});
										}}
										checkedChildren="Paid"
										unCheckedChildren="Trial"
										defaultChecked={resellerData?.defaultRegistration === 'Paid' ? true : false}
										loading={defaultRegisterPageLoading}
										disabled={isPending}
									/>
								</div>
							</>
						)}
					</>
				) : !registrationType ? (
					<>
						<h3 className="mb-4 font-semibold text-xl">Choose Registration Type</h3>
						<p className="text-sm mb-4">
							Select whether you want to set up registration for trial or paid customers.
						</p>

						<a
							onClick={() => {
								setRegistrationType('paid');
								setShowFeatureGating(false);
								setShowAutoScale(false);
							}}
						>
							<div className="border-2 border-gray-300 mt-3 p-4 rounded-lg">
								<div className="flex w-full justify-between items-center mb-3">
									<CircleDollarSign className="text-blue-600" />
									<ChevronRight className="text-gray-600 text-sm" size={18} />
								</div>
								<h4 className="text-base mb-0">Paid Registration</h4>
								<p className="my-0 text-gray-600 text-sm">
									For clients ready to purchase a subscription
								</p>
							</div>
						</a>

						<a
							onClick={() => {
								setRegistrationType('trial');
								setShowFeatureGating(false);
								setShowAutoScale(false);
							}}
						>
							<div className="border-2 border-gray-300 mt-3 p-4 rounded-lg">
								<div className="flex w-full justify-between items-center mb-3">
									<Tag className="text-blue-600" />
									<ChevronRight className="text-gray-600 text-sm" size={18} />
								</div>
								<h4 className="text-base mb-0">Trial Registration</h4>
								<p className="my-0 text-gray-600 text-sm">
									For clients who want to try the service first
								</p>
							</div>
						</a>
					</>
				) : !showFeatureGating ? (
					<>
						<div className="flex items-center mb-4">
							<Button
								type="text"
								icon={<ChevronRight className="rotate-180 w-4 h-4" />}
								onClick={() => setRegistrationType(null)}
							>
								Back
							</Button>
						</div>
						<h3 className="mb-4 font-semibold text-xl">Feature Gating</h3>
						<p className="text-sm mb-4">
							Would you like to customize which features are available to your clients during
							registration?
						</p>

						<a
							onClick={() => {
								dispatch(
									openModal({
										modalId: MODAL_IDS.FEATURE_GATING_CUSTOMIZE_MODAL,
										data: { isTrialForm: registrationType === 'trial' },
									}),
								);
							}}
						>
							<div className="border-2 border-gray-300 mt-3 p-4 rounded-lg">
								<div className="flex w-full justify-between items-center mb-3">
									<SparklesIcon className="text-blue-600" />
									<ChevronRight className="text-gray-600 text-sm" size={18} />
								</div>
								<h4 className="text-base mb-0">Customize Features</h4>
								<p className="my-0 text-gray-600 text-sm">
									Select which features will be available to your clients
								</p>
							</div>
						</a>

						<div className="flex justify-end mt-4">
							<Button
								type="primary"
								onClick={() => {
									setShowFeatureGating(true);
									if (registrationType === 'trial') {
										setShowAutoScale(true);
									}
								}}
							>
								Next
							</Button>
						</div>
					</>
				) : registrationType === 'paid' && !showAutoScale ? (
					<>
						<div className="flex items-center mb-4">
							<Button
								type="text"
								icon={<ChevronRight className="rotate-180 w-4 h-4" />}
								onClick={() => setShowFeatureGating(false)}
							>
								Back
							</Button>
						</div>
						<h3 className="mb-4 font-semibold text-xl">Auto-Scale Registration</h3>
						<p className="text-sm mb-4">
							Auto-scale automatically increases your seat count when a new client registers, even
							if you've reached your current limit. The additional seat cost will be added to your
							next bill.
						</p>

						<div className="flex items-center justify-between p-4 border-2 border-gray-300 rounded-lg">
							<div>
								<h4 className="text-base mb-1">Enable Auto-Scale</h4>
								<p className="text-sm text-gray-600 my-0">Automatically add seats when needed</p>
							</div>
							<Switch
								checked={autoScaleEnabled}
								onChange={handleAutoScaleChange}
								loading={isAutoScaleUpdating}
								disabled={isAutoScaleUpdating}
							/>
						</div>

						<div className="flex justify-end mt-4">
							<Button
								type="primary"
								onClick={() => setShowAutoScale(true)}
								disabled={isAutoScaleUpdating}
							>
								Next
							</Button>
						</div>
					</>
				) : (
					<>
						<div className="flex items-center mb-4">
							<Button
								type="text"
								icon={<ChevronRight className="rotate-180 w-4 h-4" />}
								onClick={() =>
									registrationType === 'paid'
										? setShowAutoScale(false)
										: setShowFeatureGating(false)
								}
							>
								Back
							</Button>
						</div>
						<h3 className="mb-4 font-semibold text-xl">Registration Link</h3>
						<p className="text-sm mb-4">
							{registrationType === 'trial'
								? 'Use this link to allow clients to register for a trial account.'
								: 'Use this link to allow clients to register for a paid account. Make sure to place this behind a paywall or protected area of your website.'}
						</p>

						{resellerData.customDomain &&
							(() => {
								const urlPath =
									registrationType === 'trial'
										? AppRoutes.UNBRANDED_CLIENT_TRIAL
										: AppRoutes.GENERIC_DOMAIN_REGISTER;

								const fullUrl = `https://${resellerData.customDomain}${urlPath}`;

								return (
									<>
										<div className="mb-6">
											<p className="text-sm font-medium mb-2">Custom Domain URL</p>
											<Input
												value={fullUrl}
												readOnly
												suffix={
													<CopyOutlined
														className="cursor-pointer text-gray-500 hover:text-gray-700"
														onClick={() => {
															if (linkData?.link) {
																navigator.clipboard.writeText(fullUrl);
																notification.success({ message: 'URL copied successfully' });
															}
														}}
													/>
												}
											/>
										</div>
										<Divider />
									</>
								);
							})()}

						<div className="mb-6">
							<p className="text-sm font-medium mb-2">Direct URL</p>
							<Input
								value={`${import.meta.env.VITE_APP_WIHTELABEL_URL}${
									registrationType === 'trial'
										? AppRoutes.UNBRANDED_CLIENT_TRIAL
										: AppRoutes.GENERIC_DOMAIN_REGISTER
								}?code=${linkData?.link}`}
								readOnly
								suffix={
									<CopyOutlined
										className="cursor-pointer text-gray-500 hover:text-gray-700"
										onClick={() => {
											if (linkData?.link) {
												navigator.clipboard.writeText(
													`${import.meta.env.VITE_APP_WIHTELABEL_URL}${
														registrationType === 'trial'
															? AppRoutes.UNBRANDED_CLIENT_TRIAL
															: AppRoutes.GENERIC_DOMAIN_REGISTER
													}?code=${linkData.link}`,
												);
												notification.success({ message: 'URL copied successfully' });
											}
										}}
									/>
								}
							/>
						</div>

						<Divider />

						<div>
							<p className="text-sm font-medium mb-2">Embed Iframe</p>
							<p className="text-sm text-gray-600 mb-3">
								Copy and paste this code to embed the registration form directly on your website
							</p>
							<Input
								value={`<iframe src="${import.meta.env.VITE_APP_WIHTELABEL_URL}${
									registrationType === 'trial'
										? AppRoutes.UNBRANDED_CLIENT_TRIAL
										: AppRoutes.GENERIC_DOMAIN_REGISTER
								}?code=${linkData?.link}" width="100%" height="100%" frameBorder="0"></iframe>`}
								readOnly
								suffix={
									<CopyOutlined
										className="cursor-pointer text-gray-500 hover:text-gray-700"
										onClick={() =>
											registrationType === 'trial'
												? handleTrialCopyIframe()
												: handleCopyIframe(AppRoutes.GENERIC_DOMAIN_REGISTER)
										}
									/>
								}
							/>
						</div>
					</>
				)}
			</Modal>
		</>
	);
};

export default Embed;
